/**
 * CheckinExecutionReport Component
 *
 * Exibe o relatório de execução após checkout de um check-in.
 * Mostra resumo das tarefas executadas durante a visita.
 *
 * @since 2026-01-29
 * @module tmf_checkin
 */

import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { checkinApi, formatDuration } from '@/services/checkinApi';
import type { CheckinTask } from '@/types/checkin';

interface CheckinExecutionReportProps {
  checkinId: number;
  onClose?: () => void;
}

export function CheckinExecutionReport({ checkinId, onClose }: CheckinExecutionReportProps) {
  const { t } = useTranslation('checkin');

  const { data: report, isLoading, error } = useQuery({
    queryKey: ['checkin-report', checkinId],
    queryFn: () => checkinApi.getReport(checkinId),
  });

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <div className="w-8 h-8 border-4 border-orange-500 border-t-transparent rounded-full animate-spin mb-3" />
        <p className="text-sm text-muted-foreground">
          {t('report.loading', 'Gerando relatório...')}
        </p>
      </div>
    );
  }

  if (error || !report) {
    return (
      <div className="text-center py-6">
        <div className="w-16 h-16 bg-red-100 dark:bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
          <i className="bi bi-exclamation-triangle text-red-500 text-2xl" />
        </div>
        <h3 className="text-lg font-semibold text-foreground mb-2">
          {t('report.error', 'Erro ao carregar relatório')}
        </h3>
        {onClose && (
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 bg-muted text-muted-foreground rounded-lg"
          >
            {t('common.close', 'Fechar')}
          </button>
        )}
      </div>
    );
  }

  const formatDateTime = (dateStr?: string | null) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="space-y-6">
      {/* Header com sucesso */}
      <div className="text-center">
        <div className="w-16 h-16 bg-green-100 dark:bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
          <i className="bi bi-check-lg text-green-500 dark:text-green-400 text-3xl" />
        </div>
        <h3 className="text-lg font-semibold text-foreground">
          {t('report.title', 'Checkout realizado!')}
        </h3>
        <p className="text-sm text-muted-foreground mt-1">
          {report.location_name}
        </p>
      </div>

      {/* Resumo da visita */}
      <div className="grid grid-cols-3 gap-3">
        <div className="text-center p-3 bg-muted rounded-lg">
          <div className="text-2xl font-bold text-orange-500">
            {report.summary.total_tasks}
          </div>
          <div className="text-xs text-muted-foreground">
            {t('report.tasks', 'Tarefas')}
          </div>
        </div>

        <div className="text-center p-3 bg-muted rounded-lg">
          <div className="text-2xl font-bold text-blue-500">
            {formatDateTime(report.datetime_checkin)}
          </div>
          <div className="text-xs text-muted-foreground">
            {t('report.checkin', 'Entrada')}
          </div>
        </div>

        <div className="text-center p-3 bg-muted rounded-lg">
          <div className="text-2xl font-bold text-green-500">
            {report.summary.duration_minutes
              ? formatDuration(report.summary.duration_minutes)
              : '-'}
          </div>
          <div className="text-xs text-muted-foreground">
            {t('report.duration', 'Duração')}
          </div>
        </div>
      </div>

      {/* Tarefas por tipo */}
      {Object.keys(report.summary.tasks_by_type).length > 0 && (
        <div>
          <h4 className="text-sm font-medium text-muted-foreground mb-2">
            {t('report.tasksByType', 'Tarefas por Tipo')}
          </h4>
          <div className="space-y-2">
            {Object.entries(report.summary.tasks_by_type).map(([type, count]) => (
              <div
                key={type}
                className="flex items-center justify-between p-2 bg-muted rounded-lg"
              >
                <span className="text-sm text-muted-foreground">{type}</span>
                <span className="text-sm font-medium text-foreground">
                  {count}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Lista de tarefas executadas */}
      {report.tasks.length > 0 && (
        <div>
          <h4 className="text-sm font-medium text-muted-foreground mb-2">
            {t('report.executedTasks', 'Tarefas Executadas')}
          </h4>
          <div className="max-h-48 overflow-y-auto space-y-2">
            {report.tasks.map((task) => (
              <TaskReportItem key={task.id} task={task} />
            ))}
          </div>
        </div>
      )}

      {/* Mensagem se não houver tarefas */}
      {report.tasks.length === 0 && (
        <div className="text-center py-4 bg-muted rounded-lg">
          <i className="bi bi-clipboard-x text-muted-foreground text-2xl mb-2" />
          <p className="text-sm text-muted-foreground">
            {t('report.noTasks', 'Nenhuma tarefa foi registrada durante a visita')}
          </p>
        </div>
      )}

      {/* Botão fechar */}
      {onClose && (
        <button
          type="button"
          onClick={onClose}
          className="w-full px-4 py-2.5 bg-orange-500 text-white rounded-lg font-medium hover:bg-orange-600 transition-colors"
        >
          {t('common.close', 'Fechar')}
        </button>
      )}
    </div>
  );
}

interface TaskReportItemProps {
  task: CheckinTask;
}

function TaskReportItem({ task }: TaskReportItemProps) {
  const formatTime = (dateStr?: string | null) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="flex items-center gap-3 p-2 bg-card rounded-lg border border-border">
      <div
        className="w-2 h-2 rounded-full flex-shrink-0"
        style={{ backgroundColor: task.status_color || '#9ca3af' }}
      />
      <div className="flex-1 min-w-0">
        <p className="text-sm text-muted-foreground truncate">
          {task.title || task.resource_name || 'Tarefa'}
        </p>
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        {task.status_name && (
          <span
            className="text-xs px-1.5 py-0.5 rounded"
            style={{
              backgroundColor: `${task.status_color || '#9ca3af'}20`,
              color: task.status_color || '#6b7280',
            }}
          >
            {task.status_name}
          </span>
        )}
        <span className="text-xs text-muted-foreground">
          {formatTime(task.datetime_created)}
        </span>
      </div>
    </div>
  );
}

export default CheckinExecutionReport;
